import L from 'leaflet';
import { EventBus } from '../core/EventBus.js';

const CATEGORIES = [
  { id: 'cargo',       label: 'Cargo' },
  { id: 'tanker',      label: 'Tanker' },
  { id: 'specialized', label: 'Specialized' },
  { id: 'express',     label: 'Express' }
];

/**
 * Leaflet control (top-right, alongside zoom) for filtering the
 * vesselfinder-style "other players' ships" layer (OtherShipMarker.js) —
 * per-category checkboxes plus a master "show other players' ships"
 * toggle. Deliberately does NOT filter the current player's own fleet
 * (ShipMarker.js's 'ships' layer) — that's a small, always-relevant list,
 * unlike the potentially-crowded world-wide view this exists to declutter.
 *
 * Friends-only filtering is explicitly out of scope here (no friends
 * concept exists yet — see docs/proposals/multi-world-instances.md); once
 * that ships, a "friends only" toggle belongs alongside showOtherPlayers.
 */
export class ShipFilterControl extends L.Control {
  constructor(options = {}) {
    super({ position: 'topright', ...options });
    this.showOtherPlayers = true;
    this.hiddenCategories = new Set();
  }

  onAdd() {
    const container = L.DomUtil.create('div', 'leaflet-control ship-filter-control');
    L.DomEvent.disableClickPropagation(container);
    L.DomEvent.disableScrollPropagation(container);

    container.innerHTML = `
      <div class="ship-filter-toggle" title="Ship filters">🚢</div>
      <div class="ship-filter-panel" hidden>
        <label class="ship-filter-row">
          <input type="checkbox" id="sfc-other-players" checked>
          Other players' ships
        </label>
        <div class="ship-filter-divider"></div>
        ${CATEGORIES.map(c => `
          <label class="ship-filter-row">
            <input type="checkbox" class="sfc-category" data-category="${c.id}" checked>
            ${c.label}
          </label>`).join('')}
      </div>`;

    const panel = container.querySelector('.ship-filter-panel');
    container.querySelector('.ship-filter-toggle').addEventListener('click', () => {
      panel.hidden = !panel.hidden;
    });

    container.querySelector('#sfc-other-players').addEventListener('change', (e) => {
      this.showOtherPlayers = e.target.checked;
      EventBus.emit('map:shipFiltersChanged');
    });

    container.querySelectorAll('.sfc-category').forEach(cb => {
      cb.addEventListener('change', (e) => {
        const category = e.target.dataset.category;
        if (e.target.checked) this.hiddenCategories.delete(category);
        else this.hiddenCategories.add(category);
        EventBus.emit('map:shipFiltersChanged');
      });
    });

    return container;
  }

  /** @param {{ category?: string }} ship */
  isVisible(ship) {
    if (!this.showOtherPlayers) return false;
    if (ship.category && this.hiddenCategories.has(ship.category)) return false;
    return true;
  }
}
